import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion'
import { CheckCircle2 } from 'lucide-react'
import { useI18n } from '@/store/I18nContext'

interface PricingFAQProps {
  onOpenDemo: () => void
}

export function PricingFAQ({ onOpenDemo }: PricingFAQProps) {
  const { t, language } = useI18n()

  const faqs = useMemo(
    () => [
      { q: t('faq.q1'), a: t('faq.a1') },
      { q: t('faq.q2'), a: t('faq.a2') },
      { q: t('faq.q3'), a: t('faq.a3') },
      { q: t('faq.q4'), a: t('faq.a4') },
      { q: t('faq.q5'), a: t('faq.a5') },
    ],
    [t],
  )

  const getPrice = () => {
    switch (language) {
      case 'en':
        return 'USD 99'
      case 'es':
        return 'EUR 99'
      case 'fr':
        return 'EUR 99'
      case 'pt':
      default:
        return 'R$ 399'
    }
  }

  return (
    <section className="py-20 md:py-32">
      <div className="container mx-auto px-4 max-w-6xl">
        <div id="precos" className="mb-32 animate-fade-in-up">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter leading-tight">
              {t('faq.title')}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed font-medium">
              {t('faq.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-primary/50 shadow-2xl relative overflow-hidden flex flex-col transform transition-all hover:-translate-y-1">
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-bl-lg uppercase tracking-wider shadow-sm">
                {t('faq.popular')}
              </div>
              <CardHeader className="text-center pb-4 pt-10">
                <CardTitle className="text-3xl font-extrabold tracking-tight">
                  {t('faq.proTitle')}
                </CardTitle>
                <CardDescription className="text-base font-medium mt-2">
                  {t('faq.proDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-8 flex-1">
                <div className="flex justify-center items-baseline gap-1">
                  <span className="text-5xl font-extrabold tracking-tighter">
                    {getPrice()}
                  </span>
                  <span className="text-muted-foreground font-semibold">
                    {t('faq.month')}
                  </span>
                </div>
                <ul className="space-y-4 text-left mx-auto text-sm md:text-base font-medium max-w-sm">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
                    <span>{t('faq.proF1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
                    <span>{t('faq.proF2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
                    <span>{t('faq.proF3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
                    <span>{t('faq.proF4')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
                    <span>{t('faq.proF5')}</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter className="pt-4 pb-8 px-8">
                <Button
                  onClick={onOpenDemo}
                  size="lg"
                  className="w-full h-14 text-lg font-bold tracking-tight shadow-md hover:shadow-lg transition-all"
                >
                  {t('faq.getStarted')}
                </Button>
              </CardFooter>
            </Card>

            <Card className="border-border bg-card/50 shadow-lg flex flex-col transform transition-all hover:-translate-y-1 relative">
              <CardHeader className="text-center pb-4 pt-10">
                <CardTitle className="text-3xl font-extrabold tracking-tight">
                  {t('faq.entTitle')}
                </CardTitle>
                <CardDescription className="text-base font-medium mt-2">
                  {t('faq.entDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-8 flex-1">
                <div className="flex justify-center items-baseline gap-1 h-[48px] items-center">
                  <span className="text-4xl font-extrabold tracking-tighter text-foreground">
                    {t('faq.custom')}
                  </span>
                </div>
                <ul className="space-y-4 text-left mx-auto text-sm md:text-base font-medium max-w-sm">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-muted-foreground shrink-0" />
                    <span>{t('faq.entF1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-foreground shrink-0" />
                    <span className="font-bold">{t('faq.entF2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-foreground shrink-0" />
                    <span>{t('faq.entF3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-foreground shrink-0" />
                    <span>{t('faq.entF4')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-foreground shrink-0" />
                    <span>{t('faq.entF5')}</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter className="pt-4 pb-8 px-8">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onOpenDemo}
                  className="w-full h-14 text-lg font-bold tracking-tight bg-background hover:bg-muted transition-all border-2"
                >
                  {t('faq.talkToUs')}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>

        <div id="faq" className="max-w-3xl mx-auto animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter text-center mb-8">
            {t('faq.faqTitle')}
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-bold text-lg tracking-tight hover:text-primary transition-colors">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed font-medium">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  )
}
